import { useEffect, useState } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { useSearchParams, Navigate } from 'react-router-dom'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { JarvisOrb } from '@/components/JarvisOrb'
import { HAS_AUTH, loginRequest } from '@/lib/auth'
import { usePersona } from '@/lib/personas'

// =============================================================================
// Stripe success_url lands here. URL shape:
//   /auth/callback?checkout=success&tier=pro|starter|enterprise
//
// At this point the visitor has paid Stripe but is not yet signed into Entra.
// We confirm the purchase visually, then redirect them into the sign-in /
// sign-up flow. After auth, ProvisionOnLogin → /api/users/provision
// reconciles the new (or returning) user with their Stripe customer by email.
// =============================================================================

const TIER_LABEL: Record<string, string> = {
  starter: 'Starter',
  solo: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
  empire: 'Enterprise',
}

export function CheckoutSuccess() {
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [params] = useSearchParams()
  const { persona } = usePersona()
  const [autoLoginTriggered, setAutoLoginTriggered] = useState(false)

  const checkoutStatus = params.get('checkout') ?? 'success'
  const tier = params.get('tier') ?? ''
  const tierLabel = TIER_LABEL[tier.toLowerCase()] ?? tier

  // Auto-trigger the MSAL login redirect after a short pause so the user
  // sees confirmation that their purchase went through. If they click the
  // button first we'll skip the timer. Hook MUST be declared before any
  // early returns to keep hook order stable across renders.
  useEffect(() => {
    if (!HAS_AUTH || autoLoginTriggered || isAuthenticated) return
    const t = setTimeout(() => {
      setAutoLoginTriggered(true)
      instance.loginRedirect(loginRequest).catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[checkout-success] loginRedirect failed', e)
      })
    }, 2200)
    return () => clearTimeout(t)
  }, [instance, autoLoginTriggered, isAuthenticated])

  // If they're already authenticated, just go to the dashboard — the
  // provisionUser effect in App.tsx will run and patch their subscription.
  if (isAuthenticated) {
    return <Navigate to={`/dashboard?welcome=${tier || 'pro'}`} replace />
  }

  const handleClick = () => {
    setAutoLoginTriggered(true)
    if (!HAS_AUTH) {
      window.location.href = '/dashboard'
      return
    }
    instance.loginRedirect(loginRequest).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('[checkout-success] loginRedirect failed', e)
    })
  }

  const cancelled = checkoutStatus === 'cancelled'

  return (
    <div className="min-h-screen bg-jarvis-dark flex flex-col items-center justify-center gap-8 p-6">
      <JarvisOrb state={cancelled ? 'idle' : 'thinking'} size="lg" />
      <div className="text-center max-w-xl">
        {cancelled ? (
          <>
            <h1 className="text-3xl font-semibold text-jarvis-text tracking-tight">
              Checkout cancelled
            </h1>
            <p className="text-jarvis-muted mt-3 text-sm">
              No payment was taken. Head back and try again when you're ready.
            </p>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 text-jarvis-green text-xs uppercase tracking-widest mb-3">
              <CheckCircle2 size={14} />
              Subscription confirmed
            </div>
            <h1 className="text-3xl font-semibold text-jarvis-text tracking-tight">
              Welcome to {persona.wordmark}
              {tierLabel ? <> <span className="text-jarvis-blue">{tierLabel}</span></> : null}
            </h1>
            <p className="text-jarvis-muted mt-3 text-sm">
              {autoLoginTriggered ? (
                <>Redirecting you to sign in…</>
              ) : (
                <>
                  Your subscription is active. Sign in with your email to claim it and finish setup.
                </>
              )}
            </p>
          </>
        )}
      </div>
      <button
        onClick={handleClick}
        disabled={autoLoginTriggered}
        className="px-10 py-3 bg-jarvis-blue text-white rounded-lg font-medium tracking-wide
                   hover:bg-jarvis-blue-bright disabled:opacity-60 transition-colors shadow-lg shadow-jarvis-blue/20 flex items-center gap-2"
      >
        {autoLoginTriggered && <Loader2 size={14} className="animate-spin" />}
        {cancelled ? 'Back to home' : autoLoginTriggered ? 'Redirecting…' : 'Sign in to continue'}
      </button>
      {!cancelled && (
        <p className="text-[11px] text-jarvis-muted/70">
          Use the same email you entered at checkout so we can link your subscription automatically.
        </p>
      )}
    </div>
  )
}
